/**
 * Global App state
 *
 * This is stuff that will have an effect on the global app
 * state throughout.
 */
import { default as i18n, default as i18next } from "i18next";
import { Context, createContext, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent";
import ReactGA from "react-ga4";
import { fetchAPI } from "../includes/fetch";
import { routerPushByUserType } from "../includes/utils";
import { Category } from "../types/Category";
import { UserTypes } from "../types/User";
import k from "./../i18n/keys";

const english = require("../i18n/english");

export type AppStateType = {
  language: string;
  cookiesAllowed: boolean;
  userType: UserTypes;
  online: boolean;
  sidebarOpen: boolean;
  categories?: Category[];
  activities?: Category[];
};

export type AppStateProviderType = {
  appState: AppStateType;
  GetUserTypeInt(input?);
  ChangeUserType(type: UserTypes, shouldPush?: boolean);
  ToggleSidebar(state?: boolean);
};

const AppStateContext: Context<AppStateProviderType | null> =
  createContext(null);

export const AppStateProvider = ({ children }) => {
  const [appState, setAppState] = useState<AppStateType>({
    cookiesAllowed: getCookieConsentValue() === "true",
    language: "en",
    userType: UserTypes.USER,
    online: true,
    sidebarOpen: false,
  });

  const [cookie, setCookie] = useCookies(["context"]);

  i18next.init({
    lng: appState.language,
    debug: false,
    resources: {
      en: { translation: english },
    },
  });

  const ChangeUserType = (type: UserTypes, shouldPush = true) => {
    const state = {
      ...appState,
      userType: type,
    };

    setAppState(state);
    PersistState(state);

    if (shouldPush) routerPushByUserType(type);

    window.dispatchEvent(new Event("profileSwitched"));
  };

  const PersistState = (state) => {
    localStorage.setItem("appState", JSON.stringify(state));
  };

  const ToggleSidebar = (state?: boolean | null) => {
    setAppState({
      ...appState,
      sidebarOpen: state !== null ? state : !appState.sidebarOpen,
    });
  };

  const GetUserTypeInt = (type: UserTypes) => {
    switch (type ?? appState.userType) {
      case UserTypes.USER:
        return 0;
      case UserTypes.AMEEGO:
        return 1;
      case UserTypes.SERVICE_PROVIDER:
        return 2;
    }
  };

  const FetchCategories = async () => {
    const res = await fetchAPI<Category[]>("/api/categories/services");
    const vals = await res.json();

    const formattedVals = [];

    vals?.map((category) => {
      formattedVals.push({
        label: category.name,
        options: category.children.map((category) => {
          return {
            label: category.name,
            value: category.id,
          };
        }),
      });
    });

    setAppState((appState) => {
      return {
        ...appState,
        categories: formattedVals,
      };
    });
  };

  const FetchActivities = async () => {
    const res = await fetchAPI<Category[]>("/api/categories/activities");
    const vals = await res.json();

    const formattedVals = [];

    vals?.map((category) => {
      formattedVals.push({
        label: category.name,
        options: category.children.map((category) => {
          return {
            label: category.name,
            value: category.id,
          };
        }),
      });
    });

    setAppState((appState) => {
      return {
        ...appState,
        activities: formattedVals,
      };
    });
  };

  const SetContextCookie = (type: UserTypes) => {
    let oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

    setCookie("context", GetUserTypeInt(type), {
      path: "/",
      maxAge: 31556952, // Expires after 1hr
      sameSite: true,
      httpOnly: false,
      expires: oneYearFromNow,
    });
  };

  useEffect(() => {
    //We dont want to track dev mode
    if (appState.cookiesAllowed && process.env.NEXT_PUBLIC_GA_CODE) {
      ReactGA.initialize(process.env.NEXT_PUBLIC_GA_CODE);
    }
    if (appState.cookiesAllowed) {
      const clarityId = process.env.NEXT_PUBLIC_CLARITY_ID;
      if (clarityId) {
        (function (c: any, l: any, a: string, r: string, i: string) {
          c[a] =
            c[a] ||
            function () {
              (c[a].q = c[a].q || []).push(arguments);
            };
          const t: any = l.createElement(r);
          t.async = 1;
          t.src = "https://www.clarity.ms/tag/" + i;
          const y = l.getElementsByTagName(r)[0];
          y.parentNode.insertBefore(t, y);
        })(window, document, "clarity", "script", clarityId);
      }
    }
  }, [appState.cookiesAllowed]);

  useEffect(() => {
    const appStateStorage = localStorage.getItem("appState");
    if (appStateStorage) {
      setAppState({
        ...JSON.parse(appStateStorage),
        sidebarOpen: false, //This is annoying if we dont set this
      });
    }

    //Update our initial state on a refresh
    if (appState.online !== window.navigator.onLine) {
      setAppState({
        ...appState,
        online: window.navigator.onLine,
      });
    }

    window.addEventListener("online", () => {
      setAppState({
        ...appState,
        online: true,
      });
    });

    window.addEventListener("offline", () => {
      setAppState({
        ...appState,
        online: false,
      });
    });

    const activities = FetchActivities();
    const categories = FetchCategories();
  }, []);

  useEffect(() => {
    SetContextCookie(appState.userType);
  }, [appState.userType]);

  const AcceptCookieBanner = () => {
    setAppState({
      ...appState,
      cookiesAllowed: true,
    });
  };

  return (
    <AppStateContext.Provider
      value={{ appState, ChangeUserType, GetUserTypeInt, ToggleSidebar }}
    >
      {children}
      <CookieConsent
        buttonText={i18n.t(k.COOKIE_ACCEPT)}
        onAccept={AcceptCookieBanner}
      >
        {i18n.t(k.COOKIE_BANNER)}
      </CookieConsent>
    </AppStateContext.Provider>
  );
};

export const useAppState = () => useContext(AppStateContext);
